<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end align-center">
        <div>
          <v-text-field
            :value="searchRecipe"
            append-icon="mdi-magnify"
            label="Busca recetas por nombre"
            solo
            rounded
            hide-details
            flat
            dense
            @input="search"
          />
        </div>
        <div class="mx-3">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                color="secondary"
                x-small
                depressed
                :disabled="!table.selected.length"
                @click="openDuplicateRecipe()"
              >
                <v-icon
                  small
                  color="white"
                >
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
            <span>Duplicar múltiples recetas</span>
          </v-tooltip>
        </div>
        <div class="mr-3">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                depressed
                fab
                x-small
                @click="openRecalculateRecipes"
                color="primary">
                  <v-icon small color="white">mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Recalcular precio en recetas</span>
          </v-tooltip>
        </div>
        <div class="mr-3">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                depressed
                fab
                x-small
                @click="openExportRecipes"
                color="primary">
                  <v-icon small color="white">mdi-application-export</v-icon>
              </v-btn>
            </template>
            <span>Exportar recetas</span>
          </v-tooltip>
        </div>
        <div class="mr-3">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                color="error"
                x-small
                depressed
                :disabled="!table.selected.length"
                @click="removeRecipesDialog = true"
              >
                <v-icon
                  small
                  color="white"
                >
                 mdi-delete
                </v-icon>
              </v-btn>
            </template>
            <span>Eliminar múltiples recetas</span>
          </v-tooltip>
        </div>
        <div class="mr-3">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                color="secondary"
                x-small
                depressed
                :to="{ name: 'EditRecipes' }"
              >
                <v-icon
                  small
                  color="white"
                >
                  mdi-forwardburger
                </v-icon>
              </v-btn>
            </template>
            <span>Editar múltiples recetas</span>
          </v-tooltip>
        </div>
        <NewRecipe data-cy="add-recipe" @close="load()"/>
      </div>
    </div>

    <v-flex xs12>
      <v-skeleton-loader
        v-if="overlay"
        type="table"
      />
      <v-data-table
        v-else
        :loading-text="table.title"
        :loading="table.loading"
        :server-items-length="tableTotal"
        :headers="table.headers"
        :items="tableData"
        :options="tableOptions"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50]
        }"
        :items-per-page="50"
        sort-by="name"
        show-select
        item-key="_id"
        v-model="table.selected"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:top>
          <div class="px-3 filters">
            <v-row>
              <v-col md="3">
                <div class="d-flex align-end">
                  <span class="mr-3">
                    Filtros:
                  </span>
                  <v-select
                    :value="sectionSelected"
                    :items="sections"
                    item-value="id"
                    item-text="name"
                    label="Sección"
                    clearable
                    hide-details
                    return-object
                    dense
                    no-data-text="No encontramos ninguna sección, puedes crearla en el apartado de ajustes → secciones"
                    @change="filterBySection"
                    @click:clear="filterBySection"
                  />
                </div>
              </v-col>
              <v-col md="3">
                <v-select
                  :value="statusSelected"
                  :items="status"
                  item-value="value"
                  item-text="text"
                  label="Estatus"
                  clearable
                  hide-details
                  return-object
                  dense
                  @change="filterByStatus"
                  @click:clear="filterByStatus"
                />
              </v-col>
              <v-col cols="3">
                <v-btn
                  text
                  color="primary"
                  rounded
                  small
                  :to="{ name: 'ArchivedRecipes' }">
                  Ver recetas archivadas
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </template>
        <template v-slot:item.name="{ item }">
          <div>
            <a
              v-if="(($is() === 'manager' || $is() === 'chef') && ['in-progress', 'archived', 'unattached', 'attached'].includes(item.status))"
              href="" @click.prevent="openUpdateIngredientsDialog(item)">
              {{ item.name }}
            </a>
            <span v-else>
              {{ item.name }}
            </span>
          </div>
          <div class="caption grey--text">
            <span v-if="item.internalId">
              {{ item.internalId }}
            </span>
            <span v-else>
              -
            </span>
          </div>
        </template>
        <template v-slot:item.section="{ item }">
          <span v-if="item.section">
            {{ item.section.name }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template v-slot:item.cost="{ item }">
          <div class="text-right">
            <div v-if="item.price">
              {{ item.cost | percentOf(item.price) | toFixNumber }}%
            </div>
            <div v-else>
              -
            </div>
            <div class="caption grey--text">
              ${{ item.cost | toFix2 | numberWithComma }}
            </div>
          </div>
        </template>
        <template v-slot:item.previousPrice="{ item }">
          <div class="text-right">
            <span v-if="item.previousPrice > 0">
              ${{ item.previousPrice | toFix2 | numberWithComma }}
            </span>
            <span v-else>
              -
            </span>
          </div>
        </template>
        <template v-slot:item.price="{ item }">
          <div class="text-right">
            <span v-if="item.price >= 0">
              ${{ item.price | toFix2 | numberWithComma }}
            </span>
            <span v-else>
              -
            </span>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
          <template>
            <div :class="`${statusColors[item.status]}--text`">
              {{ convertStatus(item.status)['text'] }}
            </div>
          </template>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end align-center">
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn
                  x-small
                  fab
                  depressed
                  v-on="on"
                  class="mr-1"
                  color="accent"
                >
                  <v-icon>
                    mdi-dots-vertical
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item :href="exportPDF(item)" target="_blank" v-if="$is() === 'manager' || $is() === 'chef'">
                  <v-list-item-icon>
                    <v-icon color="#26A69A">
                      mdi-file-document
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    Exportar PDF
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openAttachDialog(item)"
                  v-if="item.status === 'in-progress'
                    && $store.state.currentRestaurant.data._id
                    && ($is() === 'manager')
                    && (item.cost > 0 ||  item.price === 0)"
                >
                  <v-list-item-icon>
                    <v-icon color="info">
                      mdi-silverware-fork-knife
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ item.subrecipe ? 'Listar como producto' : 'Enviar al menú' }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openArchiveRecipeDialog(item)" v-if="($is() === 'manager' || $is() === 'chef') && ['in-progress', 'archived', 'unattached'].includes(item.status)">
                  <v-list-item-icon>
                    <v-icon  color="#78909C">
                      mdi-archive
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    Archivar receta
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openDuplicateDialog(item)" v-if="$is() === 'manager' || $is() === 'chef'">
                  <v-list-item-icon>
                    <v-icon  color="secondary">
                      mdi-content-copy
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    Duplicar receta
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openEditDialog(item)" v-if="($is() === 'manager' || $is() === 'chef') && ['in-progress', 'archived', 'unattached'].includes(item.status)">
                  <v-list-item-icon>
                    <v-icon  color="primary">
                      mdi-pencil-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    Editar receta
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openRemoveRecipeDialog(item)" v-if="($is() === 'manager' || $is() === 'chef') && ['in-progress', 'archived', 'unattached'].includes(item.status)">
                  <v-list-item-icon>
                    <v-icon  color="error">
                      mdi-delete
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    Eliminar receta
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <AttachRecipe
      :item="selectedRecipe"
      :btn="false"
      ref="attachDialog"
      @close="load"
    />

    <DuplicateRecipe
      :item="selectedRecipe"
      :btn="false"
      ref="duplicateDialog"
      @duplicated="load()"
    />

    <MatchRecipesWantsoft
      :item="selectedRecipe"
      :btn="false"
      ref="matchRecipesDialog"
      @close="load()"
    />

    <DuplicateMultiplesRecipes
      :item="table.selected.length"
      :recipes="table.selected"
      @close="closeDuplicateRecipe"
      ref="duplicateMultipleRecipeDialog"
    />

    <EditRecipe
      :item="selectedRecipe"
      :btn="false"
      ref="editDialog"
      @close="load"
    />

    <v-dialog @keydown.esc="removeRecipesDialog=false" v-model="removeRecipesDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Deseas eliminar {{ table.selected.length }} recetas?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseRemoveRecipesDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="removeMultipleRecipes">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="removeRecipeDialog=false" v-model="removeRecipeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Eliminar la receta {{ form.data.name }}?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseRemoveDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="remove">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="archiveRecipeDialog=false" v-model="archiveRecipeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Archivar la receta {{ form.data.name }}?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseArchiveDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="archiveRecipes">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
      <v-overlay :value="overlayDialog" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    </v-dialog>

    <v-dialog @keydown.esc="recalculateRecipeDialog=false" v-model="recalculateRecipeDialog" persistent max-width="450px">
      <v-card>
        <v-card-title>
          <span>¿Deseas recalcular el costo de todas las recetas?</span>
        </v-card-title>
        <v-card-text>
          <div>
            Al recalular el costo se actualizarán los precios de los ingredientes de cada receta respecto al precio contenido en la variante.
          </div>
          <br>
          <div>
            <b>NOTA:</b> este proceso puede tardar varios minutos, una vez finalizado recibiras un correo electrónico que confirma que se llevo acabo la actualización de las recetas.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseRecalculateRecipeDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="recalculateRecipes">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
      <v-overlay :value="overlayDialog" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    </v-dialog>

    <ingredientsDialog
      :recipe="recipe"
      :ingredientDialog="ingredientDialog"
      @closeIngredientDialog="closeIngredientDialog"/>

    <ExportRecipes ref="exportRecipes" :subRecipe="false"/>
  </div>
</template>

<script>
import { cloneDeep, debounce, isEqual } from 'lodash'
import ingredientsDialog from '@/components/sidePanels/RecipeDetail'
import NewRecipe from '@/components/modals/NewRecipe'
import EditRecipe from '@/components/modals/EditRecipe'
import DuplicateRecipe from '@/components/modals/DuplicateRecipe'
import MatchRecipesWantsoft from '@/components/modals/MatchRecipesWantsoft'
import DuplicateMultiplesRecipes from '@/components/modals/DuplicateMultiplesRecipes'
import AttachRecipe from '@/components/modals/AttachRecipe'
import ExportRecipes from '@/components/modals/ExportRecipesCsv'

export default {
  name: 'Recipes',
  components: {
    ingredientsDialog,
    NewRecipe,
    EditRecipe,
    AttachRecipe,
    DuplicateRecipe,
    DuplicateMultiplesRecipes,
    ExportRecipes,
    MatchRecipesWantsoft
  },
  data () {
    return {
      test: false,
      firstLoad: false,
      modal: {
        newRecipe: true,
        editRecipe: true
      },
      overlayDialog: false,
      attachDialog: false,
      recipeFormDialog: false,
      removeRecipeDialog: false,
      removeRecipesDialog: false,
      archiveRecipeDialog: false,
      recalculateRecipeDialog: false,
      ingredientDialog: false,
      section: this.$i18n.t('recipes.section'),
      recipe: {
        _id: null,
        cost: null
      },
      table: {
        title: this.$i18n.t('recipes.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('recipes.headers.name'), value: 'name' },
          { text: this.$i18n.t('recipes.headers.section'), value: 'section', sortable: false },
          { text: this.$i18n.t('recipes.headers.previousPrice'), value: 'previousPrice', sortable: false },
          { text: this.$i18n.t('recipes.headers.price'), value: 'price', sortable: false },
          { text: this.$i18n.t('recipes.headers.cost'), value: 'cost', sortable: true },
          { text: this.$i18n.t('recipes.headers.avgWeeklySale'), value: 'weeklySale', sortable: true, align: 'end' },
          { text: this.$i18n.t('recipes.headers.status'), value: 'status', sortable: true },
          { text: this.$i18n.t('recipes.headers.actions'), value: 'action', sortable: false }
        ],
        data: [],
        total: 0,
        selected: [],
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['name'],
          sortDesc: [false]
        }
      },
      form: {
        valid: false,
        search: '',
        meta: {
          unitStock: { disabled: true },
          subrecipe: { disabled: false }
        },
        data: {
          internalId: null,
          name: null,
          status: null,
          subrecipe: false,
          restaurant: null,
          area: null,
          section: null
        }
      },
      status: [
        { text: 'En elaboración', value: 'in-progress' },
        { text: 'En menú', value: 'attached' },
        { text: 'No adjuntado', value: 'unattached' }
      ],
      statusColors: {
        'in-progress': 'grey',
        'attached': 'primary',
        'unattached': 'danger'
      },
      selectedRecipe: {},
      name: '',
      sectionSelected: {},
      statusSelected: {}
    }
  },
  computed: {
    sections: function () {
      return (this.$store.state.currentRestaurant.sections.data || [])
        .filter(section => section.area && (section.area === this.$store.state.area._id || section.area._id === this.$store.state.area._id))
    },
    tableOptions () {
      return this.table.options
    },
    tableTotal () {
      return this.table.total
    },
    tableData () {
      return this.table.data
    },
    overlay () {
      return this.firstLoad
    },
    searchRecipe () {
      return this.name
    }
  },
  watch: {
    '$store.state.area': async function (val) {
      await this.load()
    }
  },
  async mounted () {
    this.firstLoad = true
    await this.load()
    this.firstLoad = false
  },
  methods: {
    exportPDF (item) {
      let url = process.env.VUE_APP_API_URL
      url += `/v1/recipes/${item._id}/download-pdf`
      url += `?subrecipe=false`
      return url
    },
    openExportRecipes () {
      this.$refs.exportRecipes.open()
    },
    openRecalculateRecipes () {
      this.recalculateRecipeDialog = true
    },
    openDuplicateRecipe () {
      this.$refs.duplicateMultipleRecipeDialog.open()
    },
    closeDuplicateRecipe () {
      this.table.selected = []
    },
    onCloseRecalculateRecipeDialog () {
      this.recalculateRecipeDialog = false
    },
    async recalculateRecipes (params = null) {
      params = {
        area: this.$store.state.area.id
      }
      try {
        await this.$api.post('/v1/recipes/recalculate-recipes', params)
        this.recalculateRecipeDialog = false
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    search: debounce(async function (val) {
      this.name = val
      await this.load()
    }, 300),
    closeIngredientDialog () {
      this.ingredientDialog = false
    },
    convertStatus (key) {
      return this.status.find(stat => stat.value === key) || ''
    },
    updateTable: async function (data) {
      const options = this.table.options
      if (!isEqual(options, data)) {
        this.table.options = data
        await this.load()
      }
    },
    async load (params = null) {
      this.table.loading = true
      try {
        params = {
          limit: this.table.options.itemsPerPage,
          skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
          sort: `${this.table.options.sortDesc[0] ? '-' : ''}${this.table.options.sortBy[0]}`,
          area: this.$store.state.area.id,
          subrecipe: false,
          section: (this.sectionSelected || {}).id,
          status: (this.statusSelected || {}).value,
          restaurant: this.$store.state.currentRestaurant.data._id
        }

        if (this.name.length >= 3) {
          params.name = `/${this.name}/i`
        }

        const recipes = await this.$api.get('/v1/recipes', params)
        this.table.data = recipes.data
        this.table.total = recipes.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    onCloseRemoveDialog: function () {
      this.removeRecipeDialog = false
    },
    onCloseArchiveDialog: function () {
      this.archiveRecipeDialog = false
    },
    openUpdateIngredientsDialog: async function (recipe) {
      this.form.data = recipe
      this.recipe = recipe
      this.ingredientDialog = true
    },
    openAttachRecipeDialog: async function (recipe) {
      this.form.data = cloneDeep(recipe)
      this.form.data.area = (recipe.area) ? recipe.area.id : []
      this.form.data.section = (recipe.section) ? recipe.section.id : []
      this.attachDialog = true
    },
    openRemoveRecipeDialog: async function (recipe) {
      this.form.data = recipe
      this.removeRecipeDialog = true
    },
    openArchiveRecipeDialog: async function (recipe) {
      this.form.data = recipe
      this.archiveRecipeDialog = true
    },
    remove: async function () {
      this.table.loading = true
      try {
        await this.$api.post(`/v1/recipes/${this.form.data.uuid}/delete`)
        await this.load()
        this.onCloseRemoveDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    getSubrecipeColor (isSubrecipe) {
      return isSubrecipe ? 'teal darken-4' : 'teal lighten-5'
    },
    getSubrecipeTextColor (isSubrecipe) {
      return isSubrecipe ? 'teal lighten-5' : 'teal darken-4'
    },
    filterBySection: debounce(async function (val) {
      this.sectionSelected = val
      await this.load()
    }, 300),
    filterByStatus: debounce(async function (val) {
      this.statusSelected = val
      await this.load()
    }, 300),
    openAttachDialog (item) {
      const recipe = cloneDeep(item)
      this.selectedRecipe = recipe
      this.$refs.attachDialog.open()
    },
    openDuplicateDialog (item) {
      const recipe = cloneDeep(item)
      this.selectedRecipe = recipe
      this.$refs.duplicateDialog.open()
    },
    openMatchRecipesDialog (item) {
      const recipe = cloneDeep(item)
      this.selectedRecipe = recipe
      this.$refs.matchRecipesDialog.open()
    },
    openEditDialog (item) {
      const recipe = cloneDeep(item)
      this.selectedRecipe = recipe
      this.$refs.editDialog.open()
    },
    removeMultipleRecipes: async function () {
      try {
        const recipes = this.table.selected.map(x => x.uuid)
        await this.$api.post(`/v1/recipes/delete-multiple-recipes`, { recipes: recipes })
        this.table.selected = []
        this.removeRecipesDialog = false
        await this.load()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    archiveRecipes: async function () {
      this.overlayDialog = true
      try {
        await this.$api.post(`/v1/recipes/${this.form.data.uuid}/archived`)
        this.onCloseArchiveDialog()
        await this.load()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlayDialog = false
    },
    onCloseRemoveRecipesDialog () {
      this.removeRecipesDialog = false
    }
  }
}
</script>
